/**
 * `[anudan_portal_profile]` — WP-32's profile and documents screen.
 *
 * The same shape `portal-subscriptions.js` (WP-31) established, for the same reason: saving a
 * profile and submitting an erasure request are WordPress-cookie-authenticated REST writes, and
 * WordPress's own cookie authentication refuses a state-changing request without the
 * `X-WP-Nonce` header a plain form cannot attach. The data-export download needs no script at
 * all — it is a plain link carrying the REST nonce as `_wpnonce` (`PortalProfileRenderer`'s own
 * docblock).
 *
 * ## When a confirmation is asked for, and when it is not
 *
 * WP-31's rule, applied here: an action a donor would perceive as irreversible is confirmed
 * before it is sent. Asking for erasure is exactly that — once an administrator acts on it, the
 * donor's details are gone — so it opens a native `<dialog>` first, and the route itself refuses
 * without the confirmation (`Rest\PortalProfileController::request_erasure()`). Saving an address,
 * a phone number or a preference is not: the donor can change any of them back the same way, so
 * the form simply saves, says what happened, and reloads.
 *
 * ## Every value shown afterwards is the server's own
 *
 * After a successful save the page reloads, exactly as `portal-subscriptions.js` does — the
 * verified-registration message, the consent "last changed" dates and the pending-erasure notice
 * are all the server's render, never this script's guess at what changed. Timestamps in
 * particular are never sent from here: `Donors\CommsConsent` takes them from the server clock.
 */

import { readConfig } from './shared/config-block.js';

readConfig( 'anudan-portal-profile' );

function config() {
	return ( window.AnudanEngine && window.AnudanEngine.config ) || {};
}

function restRoot() {
	return config().restUrl || '/wp-json/anudan/v1/';
}

function t( text ) {
	return window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text;
}

/**
 * @param {string} method
 * @param {string} path
 * @param {object} body
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
async function send( method, path, body ) {
	const response = await fetch( restRoot() + path, {
		method,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config().nonce || '',
		},
		credentials: 'same-origin',
		body: JSON.stringify( body || {} ),
	} );

	let parsed = null;

	try {
		parsed = await response.json();
	} catch ( error ) {
		parsed = null;
	}

	return { ok: response.ok, status: response.status, body: parsed };
}

/**
 * Asks for a year's statement and saves it as a file once it exists (WP-49).
 *
 * `GET /portal/statements/{year}` issues the statement on the first request and queues its PDF —
 * it never renders one inside the request — so it answers `202` until the file exists, then
 * streams it. This polls on a short interval, for a bounded time, and says plainly what is
 * happening in the section's live region. The request is a `GET`, but it is made with `fetch()`
 * and the REST nonce header rather than as a link: a plain link would show the `202` JSON in the
 * browser instead of waiting.
 *
 * @param {string} donorId
 * @param {string} year
 * @param {(message: string) => void} say
 * @param {number} [attempts]
 * @return {Promise<void>}
 */
async function downloadStatement( donorId, year, say, attempts = 60 ) {
	const url = `${ restRoot() }portal/statements/${ encodeURIComponent( year ) }?donor_id=${ encodeURIComponent( donorId ) }`;

	for ( let attempt = 0; attempt < attempts; attempt++ ) {
		const response = await fetch( url, {
			method: 'GET',
			headers: { 'X-WP-Nonce': config().nonce || '' },
			credentials: 'same-origin',
		} );

		if ( 202 === response.status ) {
			say( t( 'Your statement is being prepared. This page will update when it is ready.' ) );

			await new Promise( ( resolve ) => window.setTimeout( resolve, 2000 ) );

			continue;
		}

		if ( response.ok && ( response.headers.get( 'Content-Type' ) || '' ).includes( 'application/pdf' ) ) {
			const blob = await response.blob();
			const link = document.createElement( 'a' );

			link.href = window.URL.createObjectURL( blob );
			link.download = `giving-statement-${ year }.pdf`;
			document.body.appendChild( link );
			link.click();
			link.remove();
			window.URL.revokeObjectURL( link.href );

			say( t( 'Your statement has been downloaded.' ) );

			return;
		}

		let message = '';

		try {
			message = ( await response.json() ).message || '';
		} catch ( error ) {
			message = '';
		}

		say( message || t( 'We could not prepare your statement. Please try again later.' ) );

		return;
	}

	say( t( 'Your statement is taking longer than expected. Please try again in a few minutes.' ) );
}

/**
 * The fields this form sends. Names only, never `first_name`/`last_name`/`email` — the route
 * refuses those by name.
 */
const PROFILE_FIELDS = [
	'phone',
	'phone_country',
	'address_line1',
	'address_line2',
	'city',
	'state',
	'postcode',
	'country',
	'registration_number',
];

/**
 * Hides an element in a way no theme stylesheet can override — the identical inline-style
 * technique `payment-update.js`'s own `hide()` helper documents, since a component rule's
 * `display: … !important` otherwise out-ranks the native `hidden` attribute.
 *
 * @param {HTMLElement} el
 * @param {boolean}     hidden
 */
function setHidden( el, hidden ) {
	el.hidden = hidden;

	if ( hidden ) {
		el.style.setProperty( 'display', 'none', 'important' );
	} else {
		el.style.removeProperty( 'display' );
	}
}

/** Where a confirmation waits for the reload that follows a successful save (this script's own key). */
const NOTICE_KEY = 'anudanPortalProfileNotice';

class PortalProfile {
	/**
	 * @param {HTMLElement} root
	 */
	constructor( root ) {
		this.root = root;
		this.statusRegion = root.querySelector( '[data-anudan-profile-status]' );
		this.restoreNotice();
		this.dialog = root.querySelector( '[data-anudan-dialog="erasure"]' );

		root.querySelectorAll( '[data-anudan-statement]' ).forEach( ( button ) => {
			button.addEventListener( 'click', async () => {
				const section = button.closest( '[data-anudan-profile]' );
				const region = section ? section.querySelector( '[data-anudan-statement-status]' ) : null;
				const say = ( message ) => {
					if ( region ) {
						region.textContent = message;
					}
				};

				button.disabled = true;

				try {
					await downloadStatement( button.dataset.donorId || '', button.dataset.year || '', say );
				} finally {
					button.disabled = false;
				}
			} );
		} );

		root.querySelectorAll( '[data-anudan-profile]' ).forEach( ( section ) => {
			section.querySelectorAll( '[data-anudan-profile-form]' ).forEach( ( form ) => {
				form.addEventListener( 'submit', ( event ) => {
					event.preventDefault();
					this.save( section, form );
				} );
			} );

			this.wireEdit( section );
			section.querySelectorAll( '[data-anudan-range-form]' ).forEach( ( form ) => this.wireRange( section, form ) );

			const erasureButton = section.querySelector( '[data-anudan-action="erasure"]' );

			if ( erasureButton ) {
				erasureButton.addEventListener( 'click', () => this.openErasureDialog( section ) );
			}
		} );

		if ( this.dialog ) {
			const cancel = this.dialog.querySelector( '[data-anudan-dialog-cancel]' );

			if ( cancel ) {
				cancel.addEventListener( 'click', () => this.dialog.close() );
			}

			const form = this.dialog.querySelector( 'form[data-anudan-form="erasure"]' );

			if ( form ) {
				form.addEventListener( 'submit', ( event ) => {
					event.preventDefault();
					this.submitErasure();
				} );
			}
		}
	}

	/**
	 * P1 (0.9.40): the phone, address and registration number read as text with an "Edit" beside each, as the design draws them;
	 * "Edit" opens the form at that field. Without this script the form is simply shown.
	 *
	 * @param {HTMLElement} section
	 */
	wireEdit( section ) {
		const form = section.querySelector( '[data-anudan-edit-form]' );
		const view = section.querySelector( '[data-anudan-profile-view]' );

		if ( ! form || ! view ) {
			return;
		}

		const buttons = Array.from( section.querySelectorAll( '[data-anudan-edit]' ) );
		const cancel = form.querySelector( '[data-anudan-edit-cancel]' );
		const show = ( editing ) => {
			setHidden( form, ! editing );
			buttons.forEach( ( button ) => setHidden( button, editing ) );
		};

		show( false );

		buttons.forEach( ( button ) => {
			button.addEventListener( 'click', () => {
				show( true );

				const field = document.getElementById( button.getAttribute( 'data-anudan-edit' ) || '' );

				if ( field ) {
					field.focus();
				}
			} );
		} );

		if ( cancel ) {
			setHidden( cancel, false );
			cancel.addEventListener( 'click', () => {
				form.reset();
				show( false );

				if ( buttons[ 0 ] ) {
					buttons[ 0 ].focus();
				}
			} );
		}
	}

	/**
	 * P1 (0.9.40): the statement for any period. A period's chip fills in its dates; typing a date chooses "Choose dates"; a
	 * start after the end is said beside the field, in red, and is not sent; and the page says what the statement will hold,
	 * from the statement's own figures (`GET /portal/statements/preview`), before it is downloaded.
	 *
	 * @param {HTMLElement}     section
	 * @param {HTMLFormElement} form
	 */
	wireRange( section, form ) {
		const from = form.elements.namedItem( 'from' );
		const to = form.elements.namedItem( 'to' );
		const error = form.querySelector( '[data-anudan-date-error]' );
		const preview = form.querySelector( '[data-anudan-range-preview]' );
		const sentence = form.querySelector( '[data-anudan-range-sentence]' );
		const empty = form.querySelector( '[data-anudan-range-empty]' );
		const radios = Array.from( form.querySelectorAll( 'input[name="period"]' ) );
		const custom = radios.find( ( radio ) => 'custom' === radio.value );
		let asked = 0;

		if ( ! from || ! to ) {
			return;
		}

		const valid = () => {
			const bad = '' !== from.value && '' !== to.value && from.value > to.value;

			if ( error ) {
				setHidden( error, ! bad );
			}

			from.setAttribute( 'aria-invalid', bad ? 'true' : 'false' );

			return ! bad;
		};

		const refresh = async () => {
			if ( ! valid() || '' === from.value || '' === to.value ) {
				return;
			}

			const chosen = radios.find( ( radio ) => radio.checked );
			const ask = ++asked;
			const query = new URLSearchParams( {
				donor_id: section.dataset.anudanDonorId || '',
				period: chosen ? chosen.value : 'custom',
				from: from.value,
				to: to.value,
			} );

			let body = null;

			try {
				const response = await fetch( `${ restRoot() }portal/statements/preview?${ query.toString() }`, {
					headers: { 'X-WP-Nonce': config().nonce || '' },
					credentials: 'same-origin',
				} );

				body = response.ok ? await response.json() : null;
			} catch ( failure ) {
				body = null;
			}

			// An older answer that arrives after a newer question changes nothing.
			if ( ask !== asked || ! body ) {
				return;
			}

			const said = String( body.summary || '' );

			if ( sentence ) {
				sentence.textContent = said;
			}

			if ( preview ) {
				setHidden( preview, '' === said );
			}

			if ( empty ) {
				setHidden( empty, '' !== said );
			}
		};

		radios.forEach( ( radio ) => {
			radio.addEventListener( 'change', () => {
				if ( radio.dataset.from && radio.dataset.to ) {
					from.value = radio.dataset.from;
					to.value = radio.dataset.to;
				}

				refresh();
			} );
		} );

		[ from, to ].forEach( ( input ) => {
			input.addEventListener( 'change', () => {
				if ( custom ) {
					custom.checked = true;
				}

				refresh();
			} );
		} );

		form.addEventListener( 'submit', ( event ) => {
			if ( ! valid() ) {
				event.preventDefault();
				from.focus();
			}
		} );
	}

	/**
	 * @param {HTMLElement}     section
	 * @param {HTMLFormElement} form
	 */
	async save( section, form ) {
		const errorEl = form.querySelector( '[data-anudan-form-error]' );
		const button = form.querySelector( '[data-anudan-profile-save]' );
		const body = { donor_id: parseInt( section.dataset.anudanDonorId || '0', 10 ) };

		PROFILE_FIELDS.forEach( ( name ) => {
			const input = form.elements.namedItem( name );

			if ( input && 'value' in input ) {
				body[ name ] = String( input.value );
			}
		} );

		body.communication_preferences = {};

		form.querySelectorAll( '[data-anudan-consent-channel]' ).forEach( ( checkbox ) => {
			body.communication_preferences[ checkbox.dataset.anudanConsentChannel ] = !! checkbox.checked;
		} );

		// Batch B: the organization's own consent channels ("Stay in touch").
		body.contact_channels = {};

		form.querySelectorAll( '[data-anudan-contact-channel]' ).forEach( ( checkbox ) => {
			body.contact_channels[ checkbox.dataset.anudanContactChannel ] = !! checkbox.checked;
		} );

		if ( errorEl ) {
			errorEl.textContent = '';
			setHidden( errorEl, true );
		}

		if ( button ) {
			button.disabled = true;
		}

		const response = await send( 'PATCH', 'portal/me', body );

		if ( ! response.ok ) {
			if ( button ) {
				button.disabled = false;
			}

			if ( errorEl ) {
				errorEl.textContent = ( response.body && response.body.message ) || t( 'Something went wrong. Please try again.' );
				setHidden( errorEl, false );
			}

			return;
		}

		this.announce( response, placeOf( form ) );
		window.location.reload();
	}

	/**
	 * @param {HTMLElement} section
	 */
	openErasureDialog( section ) {
		if ( ! this.dialog ) {
			return;
		}

		this.dialog.dataset.anudanActiveDonorId = section.dataset.anudanDonorId || '';

		const errorEl = this.dialog.querySelector( '[data-anudan-dialog-error]' );

		if ( errorEl ) {
			errorEl.textContent = '';
			setHidden( errorEl, true );
		}

		this.dialog.showModal();
	}

	async submitErasure() {
		const donorId = parseInt( this.dialog.dataset.anudanActiveDonorId || '0', 10 );
		const response = await send( 'POST', 'portal/erasure-request', { donor_id: donorId, confirm: true } );

		if ( ! response.ok ) {
			const errorEl = this.dialog.querySelector( '[data-anudan-dialog-error]' );

			if ( errorEl ) {
				errorEl.textContent = ( response.body && response.body.message ) || t( 'Something went wrong. Please try again.' );
				setHidden( errorEl, false );
			}

			return;
		}

		this.dialog.close();
		this.announce( response, placeOf( this.root.querySelector( '[data-anudan-action="erasure"]' ) ) );
		window.location.reload();
	}

	/**
	 * Keeps a success's message, and the section it is about, for the page the reload brings (item 6 after rc5: it was shown
	 * once at the top of every tab, in a different place from the section saved).
	 *
	 * @param {{ok: boolean, body: any}} response
	 * @param {string}                   place    The saved section's `aria-labelledby`.
	 */
	announce( response, place ) {
		const message = ( response.body && response.body.message ) || '';

		if ( response.ok && message && place ) {
			try {
				window.sessionStorage.setItem( NOTICE_KEY, JSON.stringify( { message, place } ) );
			} catch ( error ) {
				// Storage refused (a private window): the page still shows the result, only the line is lost.
			}
		}
	}

	/**
	 * Shows, once, the confirmation the page before the reload kept: inside the section that was saved, under its title, and
	 * nowhere else. It goes when the donor moves to another tab; another screen never shows it, because it is read once.
	 */
	restoreNotice() {
		let kept = null;

		try {
			kept = JSON.parse( window.sessionStorage.getItem( NOTICE_KEY ) || 'null' );
			window.sessionStorage.removeItem( NOTICE_KEY );
		} catch ( error ) {
			kept = null;
		}

		if ( ! kept || 'string' !== typeof kept.message || 'string' !== typeof kept.place ) {
			return;
		}

		const section = Array.from( this.root.querySelectorAll( 'section[aria-labelledby]' ) ).find( ( node ) => node.getAttribute( 'aria-labelledby' ) === kept.place );

		if ( ! section ) {
			return;
		}

		const notice = document.createElement( 'p' );
		const title = section.querySelector( '.anudan-portal__card-title' );

		notice.className = 'anudan-portal__saved';
		notice.setAttribute( 'role', 'status' );
		notice.setAttribute( 'data-anudan-saved-here', '' );
		notice.textContent = kept.message;

		if ( title ) {
			title.after( notice );
		} else {
			section.prepend( notice );
		}

		document.addEventListener( 'anudan:portal-tab', () => notice.remove(), { once: true } );
	}
}

/**
 * The section a form or button belongs to, by its `aria-labelledby` (stable across the reload: built from the donor's number).
 *
 * @param {Element|null} node
 * @return {string}
 */
function placeOf( node ) {
	const section = node ? node.closest( 'section[aria-labelledby]' ) : null;

	return section ? section.getAttribute( 'aria-labelledby' ) || '' : '';
}

/**
 * Item 4 after rc5: the complete data file is built before the browser sees a byte, so the link looked as though nothing
 * happened. Pressed, it says "Preparing your file…" and cannot be pressed again until the download begins: the link carries a
 * one-time mark, the server answers it with a cookie of that name as it sends the file (`Rest\PortalProfileController::export()`),
 * and this watches for that cookie. With no answer in a minute (an error page, a refused request), the link comes back as it was.
 *
 * @param {HTMLAnchorElement} link
 * @return {void}
 */
function preparingUntilTheDownloadBegins( link ) {
	const words = link.textContent;
	let busy = false;

	link.addEventListener( 'click', ( event ) => {
		if ( busy ) {
			event.preventDefault();

			return;
		}

		const mark = Math.random().toString( 36 ).slice( 2, 10 ) + Date.now().toString( 36 );
		const url = new URL( link.href, window.location.href );
		const cookie = `anudan_ready_${ mark }`;
		const started = Date.now();

		// Read when the click's own navigation starts, after this listener.
		url.searchParams.set( 'anudan_ready', mark );
		link.href = url.toString();
		busy = true;
		// Called by its full name so translation tools find the string (they read `__( … )`, not this file's `t()`).
		link.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Preparing your file…', 'anudan' ) : 'Preparing your file…';
		link.setAttribute( 'aria-disabled', 'true' );
		link.classList.add( 'anudan-portal__link--busy' );

		const watch = window.setInterval( () => {
			const arrived = document.cookie.split( ';' ).some( ( part ) => part.trim().startsWith( `${ cookie }=` ) );

			if ( ! arrived && Date.now() - started < 60000 ) {
				return;
			}

			window.clearInterval( watch );
			document.cookie = `${ cookie }=; Max-Age=0; path=/`;
			busy = false;
			link.textContent = words;
			link.removeAttribute( 'aria-disabled' );
			link.classList.remove( 'anudan-portal__link--busy' );
		}, 250 );
	} );
}

document.querySelectorAll( '[data-anudan-portal-profile]' ).forEach( ( root ) => {
	new PortalProfile( root );
	root.querySelectorAll( 'a[data-anudan-export-link]' ).forEach( ( link ) => preparingUntilTheDownloadBegins( link ) );
} );
